import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { ReactNode } from 'react';
import { apiClient } from '../api/apiClient';

// Route structure
export interface AppRoute {
  path: string;
  title: string;
  label?: string;
  icon?: IconDefinition;
  element: React.ReactNode;
  layout?: 'main' | 'none';
  isMenu?: boolean;
  menuOrder?: number;
  noSidebar?: boolean;
}

// Route config input
interface RouteConfigInput {
  basePath: string;
  label: string;
  icon?: IconDefinition;
  listElement: ReactNode;
  formElement: ReactNode;
  excelElement: ReactNode;
  menuOrder?: number;
}

// Generate frontend route definitions
export const generateModuleRoutes = ({
  basePath,
  label,
  icon,
  listElement,
  formElement,
  excelElement,
  menuOrder = 1,
}: RouteConfigInput): AppRoute[] => {
  const base = `/${basePath}`;

  return [
    {
      path: base,
      title: label,
      label,
      icon,
      element: listElement,
      layout: 'main',
      isMenu: true,
      menuOrder,
    },
    {
      path: `${base}/create`,
      title: `Create ${label.slice(0, -1)}`,
      element: formElement,
      layout: 'main',
    },
    {
      path: `${base}/edit/:id`,
      title: `Edit ${label.slice(0, -1)}`,
      element: formElement,
      layout: 'main',
    },
    {
      path: `${base}/view/:id`,
      title: `View ${label.slice(0, -1)}`,
      element: formElement,
      layout: 'main',
    },
    {
      path: `${base}/excel-upload`,
      title: `Upload Excel`,
      element: excelElement,
      layout: 'main',
    },
  ];
};

// CRUD API options
export interface CrudApiOptions<T, TCreate, TUpdate> {
  basePath: string;
  buildIdPath?: (id: string) => string;
  batchCreatePath?: string; // Optional path for batch create
}

export interface CrudRequestOptions {
  headers?: Record<string, string>;
  queryParams?: Record<string, string | number>;
}

// Generate standard + optional batch create API
export function generateCrudApi<T, TCreate, TUpdate>({
  basePath,
  buildIdPath = (id) => `${basePath}/${id}`,
  batchCreatePath,
}: CrudApiOptions<T, TCreate, TUpdate>) {
  return {
    fetchAll: (options?: CrudRequestOptions) =>
      apiClient<T[]>({ method: 'get', url: basePath, ...(options || {}) }),
    getOne: (id: string, options?: CrudRequestOptions) =>
      apiClient<T>({ method: 'get', url: buildIdPath(id), ...(options || {}) }),
    create: (data: TCreate, options?: CrudRequestOptions) =>
      apiClient<T>({ method: 'post', url: basePath, data, ...(options || {}) }),
    update: (id: string, data: TUpdate, options?: CrudRequestOptions) =>
      apiClient<T>({ method: 'patch', url: buildIdPath(id), data, ...(options || {}) }),
    delete: (id: string, options?: CrudRequestOptions) =>
      apiClient<T>({ method: 'delete', url: buildIdPath(id), ...(options || {}) }),
    batchCreate: batchCreatePath
      ? (data: TCreate[], options?: CrudRequestOptions) =>
          apiClient<T[]>({ method: 'post', url: batchCreatePath, data, ...(options || {}) })
      : undefined,
  };
}
